/**
 * Lógica pura da tela "Consulta Mire" — sem React, sem axios.
 *
 * A tela faz o que o suporte faz hoje no Postman: pergunta à Mire como o
 * produto está AGORA e mostra a resposta. A diferença é que o JSON cru vira
 * texto legível — preço em reais, data em português, grade em tabela.
 *
 * Serve para separar as duas explicações de "não atualizou no admin":
 * se a Mire já mostra o dado novo, quem não gravou foi a integração;
 * se a Mire ainda mostra o dado velho, não há o que o Mooda importar.
 */

export const MIRE_SYSTEM = "mire";

const PRICE_FIELDS = [
  { key: "price", label: "Atacado", promo: "promotionPrice" },
  { key: "precovarejo", label: "Varejo", promo: "promocaoVarejo" },
  { key: "precoonline", label: "Online", promo: "promocaoOnline" },
];

/**
 * Empresas do combo.
 *
 * A fonte é o banco (`companies.urlExternalIntegration`), e não o switch
 * `getMireCredentials` do mire.service: assim um cliente Mire novo aparece na
 * tela sem deploy do painel. Empresa desativada fica fora.
 */
export function filterMireCompanies(companies) {
  if (!Array.isArray(companies)) return [];

  return companies
    .filter(
      (c) =>
        c &&
        String(c.urlExternalIntegration || "").trim().toLowerCase() ===
          MIRE_SYSTEM &&
        c.enable !== false
    )
    .map((c) => ({
      companyId: c.companyId,
      name: c.name || c.slug || `Empresa ${c.companyId}`,
      slug: c.slug || "",
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}

/** Monta a chamada. `{ ok: true, request }` ou `{ ok: false, msg }`. */
export function buildProductRequest({ companyId, productCode }) {
  if (companyId === undefined || companyId === null || companyId === "") {
    return { ok: false, msg: "Selecione a empresa antes de consultar." };
  }

  const code = String(productCode || "").trim();
  if (!code) {
    return {
      ok: false,
      msg: "Informe o código do produto na Mire (o mesmo que aparece como Referência do produto no admin).",
    };
  }

  return {
    ok: true,
    request: {
      method: "get",
      url: `${companyId}/${MIRE_SYSTEM}/produto/${encodeURIComponent(code)}`,
    },
  };
}

/** A chamada crua, do jeito que o suporte colaria no chamado. */
export function describeRequest(request) {
  if (!request) return "";
  return `${String(request.method || "get").toUpperCase()} /api/${request.url}`;
}

/**
 * A Mire manda número como string ("1.8000") e usa "0.0000" para "não tem".
 * Converte para Number, ou null quando o campo não veio.
 */
export function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatMoney(value) {
  const n = toNumber(value);
  if (n === null) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function formatQuantity(value) {
  const n = toNumber(value);
  if (n === null) return "—";
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
}

export function formatText(value) {
  if (value === null || value === undefined) return "—";
  const s = String(value).trim();
  return s === "" ? "—" : s;
}

/**
 * Datas da Mire vêm em dois formatos: com `Z` ("2026-09-15T12:47:08.000Z") e
 * sem timezone ("2026-09-15 12:47:08", que é hora local BRT). Tratar o segundo
 * como UTC jogaria o horário 3h para trás — mesmo erro que já mordeu a data do
 * pedido (`transformarPedidoMireImport`).
 */
export function parseMireDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const s = String(value).trim();
  const temTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(s);
  const d = new Date(temTimezone ? s : s.replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(value) {
  const d = parseMireDate(value);
  if (!d) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "hoje" / "ontem" / "há 12 dias" — é o dado que responde "não atualizou". */
export function describeAge(value, now = new Date()) {
  const d = parseMireDate(value);
  if (!d) return "";

  const dias = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (dias < 0) return "";
  if (dias === 0) return "hoje";
  if (dias === 1) return "ontem";
  return `há ${dias} dias`;
}

/** Lista de preços com o rótulo do que a empresa realmente usa em destaque. */
export function buildPrices(produto, priceField = "price", promoField = "promotionPrice") {
  return PRICE_FIELDS.map((f) => {
    const promoKey = f.key === priceField ? promoField : f.promo;
    const promo = toNumber(produto[promoKey]);

    return {
      key: f.key,
      label: f.label,
      value: formatMoney(produto[f.key]),
      // A Mire usa 0.0000 como "sem promoção" — exibir R$ 0,00 assustaria.
      promotion: promo && promo > 0 ? formatMoney(promo) : null,
      used: f.key === priceField,
    };
  });
}

/** Uma linha por variação, com estoque somado no rodapé. */
export function buildGrid(produto) {
  const rows = (produto.variants || []).map((v) => ({
    sku: formatText(v.skuId),
    color: formatText(v.color),
    size: formatText(v.size),
    storeId: v.storeId ?? null,
    stock: toNumber(v.stock) || 0,
    stockLabel: formatQuantity(v.stock),
  }));

  return {
    rows,
    totalStock: rows.reduce((acc, r) => acc + r.stock, 0),
  };
}

/** Agrupa as fotos por cor; a Mire manda até 4 URLs por cor (url1..url4). */
export function buildImages(produto) {
  return (produto.images || []).map((img) => ({
    color: formatText(img.color),
    urls: [img.url1, img.url2, img.url3, img.url4].filter(Boolean),
  }));
}

/**
 * Traduz o JSON da Mire para o que a tela desenha.
 * `entry` é um item de `consultas` (uma credencial/loja).
 */
export function formatMireProduct(entry, now = new Date()) {
  const produto = entry && entry.produto;
  if (!produto) return null;

  const grid = buildGrid(produto);

  return {
    code: formatText(produto.productId),
    title: formatText(produto.title || produto.description),
    store: entry.store || null,
    storeId: entry.storeId ?? null,
    updatedAt: formatDateTime(produto.updated),
    updatedAge: describeAge(produto.updated, now),
    createdAt: formatDateTime(produto.created),
    prices: buildPrices(produto, entry.priceField, entry.priceFieldPromotional),
    grid,
    images: buildImages(produto),
    details: [
      { label: "Categoria", value: formatText(produto.category) },
      { label: "Subcategoria", value: formatText(produto.subcategory) },
      { label: "Marca", value: formatText(produto.brand) },
      { label: "Modelo", value: formatText(produto.model) },
      { label: "Coleção", value: formatText(produto.collection) },
      { label: "Composição", value: formatText(produto.composicao) },
      { label: "Unidade", value: formatText(produto.unit) },
      { label: "Peso", value: formatText(produto.peso) },
      { label: "NCM", value: formatText(produto.ncm) },
      { label: "Custo", value: formatMoney(produto.cost) },
      { label: "Palavra-chave", value: formatText(produto.palavraChave) },
      { label: "Lançamento", value: formatText(produto.launched) },
    ],
    description: formatText(produto.descricaoCompleta),
  };
}

/**
 * A resposta traz uma entrada por credencial (cliente com várias lojas tem uma
 * apikey por loja). Devolve o que a tela precisa saber antes de desenhar.
 */
export function summarizeConsulta(data, now = new Date()) {
  const consultas = (data && data.consultas) || [];
  const encontrados = consultas
    .map((c) => formatMireProduct(c, now))
    .filter(Boolean);
  const erros = consultas.filter((c) => c.erro).map((c) => c.erro);

  if (encontrados.length === 0) {
    return {
      found: false,
      message:
        erros.length > 0
          ? "A Mire recusou a consulta deste produto. Veja os detalhes técnicos."
          : "Produto não encontrado na Mire para esta empresa. Confira o código — é o mesmo que aparece como Referência no admin.",
      products: [],
      errors: erros,
    };
  }

  const lojas = encontrados.filter((p) => p.store).length;
  return {
    found: true,
    message:
      lojas > 1 || encontrados.length > 1
        ? `Produto encontrado em ${encontrados.length} loja(s) da empresa.`
        : "Produto encontrado na Mire.",
    products: encontrados,
    errors: erros,
  };
}

/** Mensagem de erro acionável por quem não é técnico. */
export function friendlyError(err) {
  if (!err) return "Não foi possível consultar o produto.";

  if (err.code === "ECONNABORTED" || /timeout/i.test(err.message || "")) {
    return "A Mire demorou demais para responder. Tente de novo em alguns instantes.";
  }

  const status = err.response && err.response.status;
  const payload = (err.response && err.response.data) || {};
  const detail =
    typeof payload === "string"
      ? payload
      : payload.message || payload.error || "";

  if (/credenciais n[aã]o encontradas/i.test(detail)) {
    return "Esta empresa não tem credencial da Mire cadastrada no serviço de integração. Acione o time técnico.";
  }

  if (status === 404) {
    return "Consulta não encontrada no serviço de integração. Acione o time técnico.";
  }

  if (!err.response) {
    return "Não foi possível falar com o serviço de integração. Verifique a conexão e tente de novo.";
  }

  return detail
    ? `O serviço de integração recusou a consulta: ${detail}`
    : `O serviço de integração respondeu com erro ${status || ""}.`.trim();
}
